import { isOnPost, ZoneId, zoneIdForTarget } from '../../helpers/goalTargetFrame';
import { PoolZoneId, poolZoneIdForLocation } from '../../helpers/poolShotZones';
import { EventLightVm, MatchDetailVm } from '../../utils/api';
import {
    eventClock,
    eventMapPeriodKey,
    formatFakeCountLabel,
    formatPeriodLabel,
    formatShotTechniqueLabel,
    formatShotTypeLabel,
    MAP_PERIOD_LABELS,
    nameByMatchPlayerId,
    sortEventsChronological,
} from './insightsMatchStats';
import { MatchBundle } from './insightsQueries';
import { ATTACK_LABELS, AttackType, classifyShot } from './insightsTeamSplits';

/*
 * Shot and exclusion geometry. Opponent events count here even when their players are
 * placeholders: where they shoot from is scouting, who shot is not.
 *
 * Coordinates are the same normalized 0-1 pair the match report writes through
 * ShotLocation / ShotTarget, so the maps line up with what the operator clicked.
 */

export type MapOutcome =
    | 'goal'
    | 'saved'
    | 'blocked'
    | 'missed'
    | 'exclusion'
    | 'exclusionDrew'
    | 'exclusionGot'
    | 'penalty';

export interface MapPoint {
    key: string;
    x: number;
    y: number;
    outcome: MapOutcome;
    /** Flat fallback for native title / accessibility. */
    label: string;
    /** Insights match deep-link when known. */
    matchId?: number;
    matchLine?: string;
    scoreLine?: string;
    whenLine?: string;
    resultLine?: string;
    /** Shooter / excluded player when known. */
    playerLine?: string;
    /** Shot type or exclusion type label. */
    typeLine?: string;
    /** Direct / Lob / Skip. Shots only. */
    techniqueLine?: string;
    /** Direct from foul. Shots only. */
    foulLine?: string;
    /** Fakes before the shot. Hidden on 0 and on penalties. */
    fakeLine?: string;
    /** Shot assist, or exclusion pass made by. */
    assistLine?: string;
    /** Pool take coordinates (0-1), for the opposite-surface hover inset. */
    poolX?: number;
    poolY?: number;
    /** Goalmouth target coordinates (0-1), for the opposite-surface hover inset. */
    goalX?: number;
    goalY?: number;
    /** Running score at this event (computed from goals; light events do not store it). */
    scoreAtLine?: string;
    /** Exclusion (or similar) that is a penalty - ring on the drew/got color. */
    isPenalty?: boolean;
}

export type MapPointContext = Pick<
    MapPoint,
    | 'matchId'
    | 'matchLine'
    | 'scoreLine'
    | 'scoreAtLine'
    | 'whenLine'
    | 'resultLine'
    | 'playerLine'
    | 'typeLine'
    | 'techniqueLine'
    | 'foulLine'
    | 'fakeLine'
    | 'assistLine'
    | 'poolX'
    | 'poolY'
    | 'goalX'
    | 'goalY'
    | 'label'
>;

export interface MapSide {
    /** Points with usable coordinates. */
    points: MapPoint[];
    /** Events of this kind in the sample, whether or not they carry a location. */
    total: number;
}

export const emptyMapSide = (): MapSide => ({ points: [], total: 0 });

const SHOT_OUTCOMES: MapOutcome[] = ['goal', 'saved', 'blocked', 'missed'];

export type ShotResultKey = 'scored' | 'saved' | 'post' | 'out';

export interface ShotResultTally {
    total: number;
    scored: number;
    missed: number;
    saved: number;
    post: number;
    out: number;
}

const targetOf = (point: MapPoint, surface: 'pool' | 'goal'): { x?: number; y?: number } =>
    surface === 'goal' ? { x: point.x, y: point.y } : { x: point.goalX, y: point.goalY };

/** Goal / save first; post is a near-frame miss; everything else unsuccessful is out of target. */
export const shotResultOf = (point: MapPoint, surface: 'pool' | 'goal'): ShotResultKey => {
    if (point.outcome === 'goal') return 'scored';
    if (point.outcome === 'saved') return 'saved';
    const t = targetOf(point, surface);
    if (isOnPost(t.x, t.y)) return 'post';
    return 'out';
};

export const tallyShotResults = (
    side: MapSide,
    surface: 'pool' | 'goal'
): ShotResultTally | undefined => {
    const shots = side.points.filter((p) => SHOT_OUTCOMES.includes(p.outcome));
    if (shots.length === 0) return undefined;
    const tally: ShotResultTally = {
        total: shots.length,
        scored: 0,
        missed: 0,
        saved: 0,
        post: 0,
        out: 0,
    };
    shots.forEach((point) => {
        const key = shotResultOf(point, surface);
        if (key === 'scored') tally.scored += 1;
        else {
            tally.missed += 1;
            if (key === 'saved') tally.saved += 1;
            else if (key === 'post') tally.post += 1;
            else tally.out += 1;
        }
    });
    return tally;
};

const pctOf = (n: number, total: number): string => `${Math.round((n / total) * 100)}%`;

const part = (label: string, n: number, total: number): string =>
    `${label} ${n} (${pctOf(n, total)})`;

/** Located shots only. Unlocated are dropped. */
export const formatShotResultCoverage = (tally: ShotResultTally): string => {
    const { total, scored, missed, saved, post, out } = tally;
    const missBits = [
        saved > 0 ? part('Saved', saved, total) : undefined,
        post > 0 ? part('Posts', post, total) : undefined,
        out > 0 ? part('Out of target', out, total) : undefined,
    ].filter(Boolean);
    const missedLine = `${part('Missed', missed, total)}${
        missBits.length ? `: ${missBits.join(' · ')}` : ''
    }`;
    return `${part('Scored', scored, total)} · ${missedLine}`;
};

const usable = (x?: number, y?: number): boolean =>
    x != null && y != null && x >= 0 && x <= 1 && y >= 0 && y <= 1;

export const shotOutcome = (event: EventLightVm): MapOutcome => {
    const shot = event.shot;
    if (!shot) return 'missed';
    if (shot.isGoal) return 'goal';
    if (shot.savedById != null) return 'saved';
    if (shot.blockedById != null) return 'blocked';
    return 'missed';
};

const formatMatchDay = (startDate: Date | string | undefined): string | undefined => {
    if (!startDate) return undefined;
    const d = startDate instanceof Date ? startDate : new Date(startDate);
    if (Number.isNaN(d.getTime())) return undefined;
    const day = String(d.getDate()).padStart(2, '0');
    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ];
    return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

/**
 * Running home:away after each event (goals count after a goal event; otherwise score so far).
 * Light events do not carry a score field - this is derived.
 */
export const buildScoreAtMomentByEventId = (
    detail: MatchDetailVm,
    events: EventLightVm[]
): Map<number, string> => {
    const byId = new Map<number, string>();
    let home = 0;
    let away = 0;

    sortEventsChronological(events).forEach((event: EventLightVm) => {
        if (event.shot?.isGoal) {
            if (event.shot.teamId === detail.homeTeamId) home += 1;
            else if (event.shot.teamId === detail.awayTeamId) away += 1;
        }
        byId.set(event.id, `${home} : ${away}`);
    });

    return byId;
};

export const matchFinalScoreLine = (
    detail: MatchDetailVm,
    events: EventLightVm[]
): string | undefined => {
    if (detail.homeTeamGoalsTotal != null || detail.awayTeamGoalsTotal != null) {
        return `${detail.homeTeamGoalsTotal ?? '-'} : ${detail.awayTeamGoalsTotal ?? '-'}`;
    }
    // Fallback when analysis totals are missing: count goals from light events.
    let home = 0;
    let away = 0;
    events.forEach((event: EventLightVm) => {
        if (!event.shot?.isGoal) return;
        if (event.shot.teamId === detail.homeTeamId) home += 1;
        else if (event.shot.teamId === detail.awayTeamId) away += 1;
    });
    if (home === 0 && away === 0) return undefined;
    return `${home} : ${away}`;
};

/** Shared hover/click context for every Insights map dot. */
export const mapPointContext = (
    detail: MatchDetailVm,
    event: EventLightVm,
    resultLine: string,
    names?: Map<number, string>,
    scoreAt?: string,
    finalScore?: string
): MapPointContext => {
    const roster = names || nameByMatchPlayerId(detail);
    const home = detail.homeTeamDisplayName || 'Home';
    const away = detail.awayTeamDisplayName || 'Away';
    const day = formatMatchDay(detail.startDate);
    const matchLine = day ? `${home} vs ${away} · ${day}` : `${home} vs ${away}`;
    const scoreLine = finalScore;
    const periodKey = eventMapPeriodKey(event);
    const period = periodKey
        ? MAP_PERIOD_LABELS[periodKey]
        : formatPeriodLabel(event.period, event.periodId);
    const clock = eventClock(event);
    const whenLine = [period, clock].filter(Boolean).join(' · ');

    let playerLine: string | undefined;
    let typeLine: string | undefined;
    let techniqueLine: string | undefined;
    let foulLine: string | undefined;
    let fakeLine: string | undefined;
    let assistLine: string | undefined;
    let poolX: number | undefined;
    let poolY: number | undefined;
    let goalX: number | undefined;
    let goalY: number | undefined;

    if (event.shot) {
        typeLine = formatShotTypeLabel(event.shot.type, event.shot.isFastBreak);
        techniqueLine = formatShotTechniqueLabel(event.shot.shotTechnique);
        if (event.shot.isDirectFromFoul) foulLine = 'Direct from foul';
        fakeLine = formatFakeCountLabel(event.shot.fakeCount, event.shot.type);
        if (event.shot.takenById != null) {
            const shooter = roster.get(event.shot.takenById);
            if (shooter) playerLine = shooter;
        }
        if (event.shot.assistedById != null) {
            const assist = roster.get(event.shot.assistedById);
            if (assist) assistLine = `Assist: ${assist}`;
        }
        if (usable(event.shot.locationX, event.shot.locationY)) {
            poolX = event.shot.locationX as number;
            poolY = event.shot.locationY as number;
        }
        if (usable(event.shot.targetX, event.shot.targetY)) {
            goalX = event.shot.targetX as number;
            goalY = event.shot.targetY as number;
        }
    } else if (event.exclusion) {
        typeLine = (event.exclusion.type || 'Exclusion').replace(/_/g, ' ');
        const excluded =
            event.exclusion.excludedPlayerId != null
                ? roster.get(event.exclusion.excludedPlayerId)
                : undefined;
        const fouled =
            event.exclusion.fouledPlayerId != null
                ? roster.get(event.exclusion.fouledPlayerId)
                : undefined;
        if (excluded && fouled) playerLine = `${excluded} (on ${fouled})`;
        else if (excluded) playerLine = excluded;
        else if (fouled) playerLine = `Drawn by ${fouled}`;
        if (event.exclusion.assistedById != null) {
            const passer = roster.get(event.exclusion.assistedById);
            if (passer) assistLine = `Pass made by: ${passer}`;
        }
        if (usable(event.exclusion.locationX, event.exclusion.locationY)) {
            poolX = event.exclusion.locationX as number;
            poolY = event.exclusion.locationY as number;
        }
    }

    const label = [
        matchLine,
        whenLine,
        playerLine,
        typeLine,
        techniqueLine,
        foulLine,
        fakeLine,
        assistLine,
        resultLine,
        scoreAt ? `Score ${scoreAt}` : undefined,
        scoreLine ? `Final ${scoreLine}` : undefined,
    ]
        .filter(Boolean)
        .join(' · ');
    return {
        matchId: detail.id,
        matchLine,
        scoreLine,
        scoreAtLine: scoreAt,
        whenLine,
        resultLine,
        playerLine,
        typeLine,
        techniqueLine,
        foulLine,
        fakeLine,
        assistLine,
        poolX,
        poolY,
        goalX,
        goalY,
        label,
    };
};

export interface MapCollection {
    shotsFor: MapSide;
    shotsAgainst: MapSide;
    exclusionsAgainst: MapSide;
    targetsFor: MapSide;
    targetsAgainst: MapSide;
}

const emptyCollection = (): MapCollection => ({
    shotsFor: emptyMapSide(),
    shotsAgainst: emptyMapSide(),
    exclusionsAgainst: emptyMapSide(),
    targetsFor: emptyMapSide(),
    targetsAgainst: emptyMapSide(),
});

const push = (
    side: MapSide,
    key: string,
    x: number | undefined,
    y: number | undefined,
    outcome: MapOutcome,
    ctx: MapPointContext
): void => {
    side.total += 1;
    if (!usable(x, y)) return;
    side.points.push({
        key,
        x: x as number,
        y: y as number,
        outcome,
        ...ctx,
    });
};

/**
 * "For" is the selected club, "against" is whoever they played. Exclusions are collected
 * for the punished opponent, which is what a coach wants to see when preparing a press.
 */
export const collectMapPoints = (bundles: MatchBundle[], ourTeamIds: number[]): MapCollection => {
    const result = emptyCollection();

    bundles.forEach((bundle: MatchBundle) => {
        const names = nameByMatchPlayerId(bundle.detail);
        const scoreAt = buildScoreAtMomentByEventId(bundle.detail, bundle.events);
        const finalScore = matchFinalScoreLine(bundle.detail, bundle.events);
        bundle.events.forEach((event: EventLightVm) => {
            if (event.shot) {
                const ours = ourTeamIds.includes(event.shot.teamId);
                const outcome = shotOutcome(event);
                const attack: AttackType = classifyShot(event.shot);
                const resultLine = `${ATTACK_LABELS[attack]} · ${outcome}`;
                const ctx = mapPointContext(
                    bundle.detail,
                    event,
                    resultLine,
                    names,
                    scoreAt.get(event.id),
                    finalScore
                );
                const key = `${bundle.detail.id}-${event.id}`;

                push(
                    ours ? result.shotsFor : result.shotsAgainst,
                    key,
                    event.shot.locationX,
                    event.shot.locationY,
                    outcome,
                    ctx
                );
                push(
                    ours ? result.targetsFor : result.targetsAgainst,
                    `${key}-t`,
                    event.shot.targetX,
                    event.shot.targetY,
                    outcome,
                    ctx
                );
            }

            if (event.exclusion && !ourTeamIds.includes(event.exclusion.teamId)) {
                push(
                    result.exclusionsAgainst,
                    `${bundle.detail.id}-${event.id}-x`,
                    event.exclusion.locationX,
                    event.exclusion.locationY,
                    'exclusion',
                    mapPointContext(
                        bundle.detail,
                        event,
                        event.exclusion.type || 'Exclusion',
                        names,
                        scoreAt.get(event.id),
                        finalScore
                    )
                );
            }
        });
    });

    return result;
};

export const pointsInGoalZone = (points: MapPoint[], zone: ZoneId): MapPoint[] =>
    points.filter((point) => zoneIdForTarget(point.x, point.y) === zone);

export const pointsInPoolZone = (points: MapPoint[], zone: PoolZoneId): MapPoint[] =>
    points.filter((point) => poolZoneIdForLocation(point.x, point.y) === zone);

const remapOpposite = (
    points: MapPoint[],
    pick: (point: MapPoint) => { x?: number; y?: number },
    keySuffix: string
): MapSide => ({
    total: points.length,
    points: points
        .map((point) => {
            const xy = pick(point);
            if (xy.x == null || xy.y == null || xy.x < 0 || xy.x > 1 || xy.y < 0 || xy.y > 1) {
                return undefined;
            }
            return { ...point, key: `${point.key}-${keySuffix}`, x: xy.x, y: xy.y };
        })
        .filter((point): point is MapPoint => point != null),
});

/** Goal-zone click: same shots, plotted on the pool. */
export const asOppositePoolSide = (points: MapPoint[]): MapSide =>
    remapOpposite(points, (point) => ({ x: point.poolX, y: point.poolY }), 'opp-pool');

/** Pool-zone click: same shots, plotted on the goalmouth. */
export const asOppositeGoalSide = (points: MapPoint[]): MapSide =>
    remapOpposite(points, (point) => ({ x: point.goalX, y: point.goalY }), 'opp-goal');
